import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  approveSuspect, correctSuspect, fetchSuspectQueue, rejectSuspect, runSuspectScan,
} from "../api/adminSuspectApi";
import { Section } from "../components/admin/AdminUI";

const REASON_LABELS = {
  price_placeholder: "Platzhalterpreis",
  price_below_floor: "Preis unter Mindestgrenze",
  price_conflict_description: "Preis ≠ Beschreibung",
  unit_price_implausible: "€/m² unplausibel",
  price_looks_like_rate: "Preis wirkt wie Rate",
  extreme_market_deviation: "Extreme Marktabweichung",
  incomplete_data: "Unvollständige Daten",
  damage_detected: "Schaden erkannt",
  vision_damage_suspected: "KI-Vision: Schaden",
  admin_rejected: "Admin abgelehnt",
};

const fmtEUR = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

export default function AdminSuspectQueuePage() {
  const qc = useQueryClient();
  const [vertical, setVertical] = useState("real_estate");
  const [scanJob, setScanJob] = useState(null); // V4.23 async scan job
  const [reason, setReason] = useState(null);
  const [review, setReview] = useState("pending");
  const [offset, setOffset] = useState(0);
  const limit = 25;

  const queueQ = useQuery({
    queryKey: ["admin", "suspect-queue", vertical, reason, review, offset],
    queryFn: () => fetchSuspectQueue({ vertical, reason: reason || undefined, review, limit, offset }),
    keepPreviousData: true,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "suspect-queue"] });

  const scanMut = useMutation({
    mutationFn: () => runSuspectScan({ vertical }),
    // V4.23 — scan is now an async maintenance job: poll until done.
    onSuccess: async (d) => {
      if (!d?.job_ulid) {
        toast.success("Scan gestartet");
        invalidate();
        return;
      }
      toast.info("Scan gestartet — läuft im Hintergrund…");
      setScanJob({ ulid: d.job_ulid, status: "queued" });
      const { getMaintenanceJob } = await import("../api/adminApi");
      const t0 = Date.now();
      const poll = async () => {
        try {
          const job = await getMaintenanceJob(d.job_ulid);
          const st = job?.status || job?.job?.status;
          const metrics = job?.metrics || job?.job?.metrics || {};
          setScanJob({ ulid: d.job_ulid, status: st, metrics });
          if (st === "completed") {
            toast.success(
              `Scan fertig: ${metrics.scanned ?? "?"} geprüft, ${metrics.flagged ?? metrics.quarantined ?? 0} geflaggt`,
            );
            setScanJob(null);
            invalidate();
            return;
          }
          if (st === "failed") {
            toast.error("Scan fehlgeschlagen — Details im Maintenance-Log.");
            setScanJob(null);
            return;
          }
        } catch {
          /* transient poll error — keep polling */
        }
        if (Date.now() - t0 < 10 * 60 * 1000) setTimeout(poll, 2500);
        else setScanJob(null);
      };
      setTimeout(poll, 2000);
    },
    onError: (e) => {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409) {
        toast.warning("Ein Scan läuft bereits — bitte warten.");
      } else {
        toast.error(`Scan fehlgeschlagen: ${detail?.code || e?.message || e}`);
      }
    },
  });

  const data = queueQ.data;
  const items = data?.items || [];
  const breakdown = data?.reason_breakdown || {};
  const total = data?.total ?? 0;

  return (
    <div data-testid="admin-suspect-queue" className="space-y-6">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
            /api/admin/suspect-listings
          </span>
          <h1 className="text-2xl text-zinc-100 font-medium mt-1">Quarantäne-Queue</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Verdächtige Listings (Platzhalterpreise, Parsing-Fehler, extreme Abweichungen) sind aus
            DealFlow, Top Deals, Heatmap &amp; KI ausgeschlossen. Hier freigeben, ablehnen oder korrigieren —
            Korrekturen überleben Re-Imports.
          </p>
        </div>
        <button
          data-testid="suspect-scan-btn"
          onClick={() => scanMut.mutate()}
          disabled={scanMut.isPending || !!scanJob}
          className="px-4 py-2 rounded-md text-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50 transition-colors"
        >
          {scanJob
            ? `Scan läuft… (${scanJob.status}${scanJob.metrics?.scanned ? ` · ${scanJob.metrics.scanned} geprüft` : ""})`
            : scanMut.isPending
              ? "Starte Scan…"
              : "Scan jetzt ausführen"}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        {[["real_estate", "Immobilien"], ["auto", "Autos"]].map(([v, label]) => (
          <button
            key={v}
            data-testid={`suspect-vertical-${v}`}
            onClick={() => { setVertical(v); setReason(null); setOffset(0); }}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              vertical === v
                ? "border-emerald-500/60 bg-emerald-500/15 text-emerald-200"
                : "border-zinc-700 text-zinc-400 hover:bg-zinc-800/60"
            }`}
          >
            {label}
          </button>
        ))}
        <div className="w-px h-5 bg-zinc-800 mx-1" />
        {[["pending", "Offen"], ["rejected", "Abgelehnt"]].map(([r, label]) => (
          <button
            key={r}
            data-testid={`suspect-review-${r}`}
            onClick={() => { setReview(r); setOffset(0); }}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
              review === r
                ? "border-sky-500/60 bg-sky-500/15 text-sky-200"
                : "border-zinc-700 text-zinc-400 hover:bg-zinc-800/60"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Reason breakdown chips */}
      <div className="flex flex-wrap gap-2" data-testid="suspect-reason-breakdown">
        <button
          onClick={() => { setReason(null); setOffset(0); }}
          className={`px-2.5 py-1 rounded-md text-[11px] border ${!reason ? "border-emerald-500/50 text-emerald-200 bg-emerald-500/10" : "border-zinc-800 text-zinc-400 hover:bg-zinc-800/50"}`}
        >
          Alle · {total}
        </button>
        {Object.entries(breakdown).sort((a, b) => b[1] - a[1]).map(([slug, n]) => (
          <button
            key={slug}
            data-testid={`suspect-reason-chip-${slug}`}
            onClick={() => { setReason(reason === slug ? null : slug); setOffset(0); }}
            className={`px-2.5 py-1 rounded-md text-[11px] border ${reason === slug ? "border-amber-500/60 text-amber-200 bg-amber-500/10" : "border-zinc-800 text-zinc-400 hover:bg-zinc-800/50"}`}
          >
            {REASON_LABELS[slug] || slug} · {n}
          </button>
        ))}
      </div>

      <Section title={`${total} Listings in Quarantäne`} testid="suspect-queue-section">
        {queueQ.isLoading ? (
          <div className="text-xs text-zinc-500" data-testid="suspect-queue-loading">Lade Queue…</div>
        ) : items.length === 0 ? (
          <div className="text-xs text-zinc-500" data-testid="suspect-queue-empty">
            Keine verdächtigen Listings in dieser Ansicht. 🎉
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((it) => (
              <QueueRow key={it.ulid} item={it} vertical={vertical} onDone={invalidate} />
            ))}
          </div>
        )}
        {total > limit && (
          <div className="flex items-center justify-between mt-4 text-xs text-zinc-400">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
              className="px-3 py-1.5 rounded border border-zinc-700 disabled:opacity-40 hover:bg-zinc-800/60"
              data-testid="suspect-queue-prev"
            >
              ← Zurück
            </button>
            <span>{offset + 1}–{Math.min(offset + limit, total)} von {total}</span>
            <button
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
              className="px-3 py-1.5 rounded border border-zinc-700 disabled:opacity-40 hover:bg-zinc-800/60"
              data-testid="suspect-queue-next"
            >
              Weiter →
            </button>
          </div>
        )}
      </Section>
    </div>
  );
}

function QueueRow({ item, vertical, onDone }) {
  const [showCorrect, setShowCorrect] = useState(false);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const d = item.details || {};

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Fehler");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`suspect-row-${item.ulid}`}
      className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-3 flex flex-col gap-2"
    >
      <div className="flex gap-3">
        <div className="w-24 h-16 rounded-md overflow-hidden bg-zinc-900 shrink-0">
          {item.primary_image_url ? (
            <img src={item.primary_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[10px] text-zinc-600">kein Bild</div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <a
              href={item.source_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-zinc-200 hover:text-emerald-300 truncate"
              title={item.title}
            >
              {item.title || "(ohne Titel)"} ↗
            </a>
            <span className="text-sm text-emerald-300 font-medium tabular-nums shrink-0">
              {fmtEUR(item.price)}
            </span>
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5">
            {item.country} · {item.source_platform}
            {vertical === "real_estate" && (
              <> · {d.object_type_canonical || "?"} · {d.transaction_type || "?"}{d.living_area_m2 ? ` · ${Math.round(d.living_area_m2)} m²` : ""}{d.city ? ` · ${d.city}` : ""}</>
            )}
            {vertical === "auto" && (
              <> · {d.make_canonical || "?"} {d.model_family || ""}{d.year ? ` · ${d.year}` : ""}{d.mileage_km ? ` · ${Math.round(d.mileage_km / 1000)}tkm` : ""}</>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {(item.suspect_reasons || []).map((r) => (
              <span key={r} className="px-1.5 py-0.5 rounded text-[10px] border border-amber-500/40 bg-amber-500/10 text-amber-200">
                {REASON_LABELS[r] || r}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-900">
        <button
          data-testid={`suspect-approve-${item.ulid}`}
          disabled={busy}
          onClick={() => act(() => approveSuspect(item.ulid, null), "Freigegeben — Listing ist wieder sichtbar")}
          className="px-3 py-1 rounded text-[11px] border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 disabled:opacity-40"
        >
          ✓ Freigeben
        </button>
        <button
          data-testid={`suspect-reject-${item.ulid}`}
          disabled={busy}
          onClick={() => act(() => rejectSuspect(item.ulid, null), "Abgelehnt — bleibt dauerhaft ausgeblendet")}
          className="px-3 py-1 rounded text-[11px] border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
        >
          ✕ Ablehnen
        </button>
        <button
          data-testid={`suspect-correct-toggle-${item.ulid}`}
          onClick={() => setShowCorrect((v) => !v)}
          className="px-3 py-1 rounded text-[11px] border border-sky-500/40 text-sky-200 hover:bg-sky-500/10"
        >
          ✎ Preis korrigieren
        </button>
        {item.description_excerpt && (
          <span className="text-[10px] text-zinc-600 truncate max-w-md" title={item.description_excerpt}>
            „{item.description_excerpt.slice(0, 120)}…"
          </span>
        )}
      </div>
      {showCorrect && (
        <div className="flex items-center gap-2 pt-1">
          <input
            data-testid={`suspect-correct-price-${item.ulid}`}
            type="number"
            min="1"
            placeholder="Korrekter Preis in €"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-44 px-2 py-1.5 rounded bg-zinc-900 border border-zinc-700 text-xs text-zinc-100 focus:border-sky-500 outline-none"
          />
          <button
            data-testid={`suspect-correct-save-${item.ulid}`}
            disabled={busy || !price || Number(price) <= 0}
            onClick={() =>
              act(
                () => correctSuspect(item.ulid, { price: Number(price) }),
                "Korrigiert + freigegeben — Korrektur überlebt Re-Imports"
              )
            }
            className="px-3 py-1.5 rounded text-[11px] border border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20 disabled:opacity-40"
          >
            Speichern &amp; freigeben
          </button>
        </div>
      )}
    </div>
  );
}
